class CartDrawer extends HTMLElement {
	constructor() {
		super();

		this.addEventListener("keyup", (evt) => {
			if (evt.code !== "Escape") return;
			const inner = this.querySelector('.drawer__inner');
			if (!this.classList.contains('active') || !inner || inner.inert || document.querySelector('dialog[open]')) return;
			const active = document.activeElement;
			if (evt.target.closest('[role="dialog"], dialog') !== inner ||
				(active && active !== document.body && active.closest('[role="dialog"], dialog') !== inner)) return;
			// 兼容仅发送 keyup 的旧调用；不得越过子对话框或重复关闭已收尾的抽屉。
			this.close();
		});
		this.addEventListener('click', event => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
    });
		this.onTrapKeydown = this.onTrapKeydown.bind(this);
		this.setHeaderCartIconAccessibility();
	}

	/*
	 * 抽屉开着时的键盘兜底（a11y）。global.js 的 trapFocus 现在每次 Tab 也会现算可见元素，
	 * 但它的 keydown 只在焦点位于容器内时挂着，且不区分抽屉里叠着的子对话框（备注弹窗）。
	 * 这里补两件 trapFocus 做不到的事：焦点掉到 body 时 Tab 拉回抽屉、Esc 关抽屉；
	 * 只遍历归属 drawer__inner 本身的控件（不含嵌套 [role=dialog]）。
	 * 叠在上面的弹窗自己管焦点：快速购买会把 drawer__inner 设 inert，购物车编辑器是原生 dialog，
	 * 抽屉里的备注弹窗是另一个 [role=dialog] —— 这些情况一律不插手。
	 */
	onTrapKeydown(event) {
		if (event.key !== 'Tab' && event.key !== 'Escape') return;
		const inner = this.querySelector('.drawer__inner');
		if (!this.classList.contains('active') || !inner || inner.inert || document.querySelector('dialog[open]')) return;
		const active = document.activeElement;
		const lost = !active || active === document.body;
		if (!lost && active.closest('[role="dialog"], dialog') !== inner) return;

		if (event.key === 'Escape') {
			// 在已确认归属的 keydown 就关闭，避免 Safari 中途失焦后 keyup 落到 body 而漏关。
			event.preventDefault();
			this.close();
			return;
		}

		// 空车也用整个 inner：关闭按钮在 header 里，不在 .drawer__inner-empty 里；隐藏的部分靠下面的可见性过滤掉
		// getFocusableElements 统一排除闭合 details 等不可聚焦内容，这里保留对话框归属检查。
		const items = getFocusableElements(inner).filter(el =>
			el.closest('[role="dialog"], dialog') === inner
		);
		if (!items.length) return;
		// 整个 Tab 顺序由这里接管（按可见元素的 DOM 顺序走，首尾循环），并截住 trapFocus 的 keydown，
		// 避免两份处理各移一次焦点。
		event.preventDefault();
		event.stopImmediatePropagation();
		const index = items.indexOf(active);
		let next;
		if (index === -1) next = event.shiftKey ? items[items.length - 1] : items[0];
		else next = items[(index + (event.shiftKey ? -1 : 1) + items.length) % items.length];
		next.focus();
	}

	setHeaderCartIconAccessibility() {
		const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;
		cartLink.setAttribute("role", "button");
		cartLink.setAttribute("aria-haspopup", "dialog");

		cartLink.addEventListener("click", (event) => {
			event.preventDefault();
			this.open(cartLink);
		});

		cartLink.addEventListener("keydown", (event) => {
			if (event.code.toUpperCase() === "SPACE") {
				event.preventDefault();
				this.open(cartLink);
			}
		});
	}

	open(triggeredBy) {
    if (CartFlow.displayStale) { CartFlow.recoverDisplay(true); return; }
    if (CartFlow.uncertain) { window.location.assign(CartFlow.url('cart')); return; }
    this.cancelNotificationCleanup?.();
    if (this.classList.contains("active")) return;
    this.cancelOpenMotion?.();
    const ownsFocus = claimThemeDrawerFocus();
		if (triggeredBy) this.setActiveElement(triggeredBy);
		const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
		if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
			this.setSummaryAccessibility(cartDrawerNote);
		// here the animation doesn't seem to always get triggered. A timeout seem to help
		this.classList.add("animate", "active");
		document.addEventListener("keydown", this.onTrapKeydown, true); // 捕获阶段：保证先于 trapFocus 的 document 监听

		this.cancelOpenMotion = waitForThemeMotion(this.querySelector('.drawer__inner'), () => {
        if (!this.isConnected || !this.classList.contains("active") || !ownsFocus()) return;
				// 空购物车的正文不包含标题栏关闭按钮；焦点锁必须覆盖整个实际 dialog。
				const containerToTrapFocusOn = this.querySelector('.drawer__inner') || document.getElementById('CartDrawer');
				const focusElement =
					this.querySelector(".drawer__inner") ||
					this.querySelector(".drawer__close");
				trapFocus(containerToTrapFocusOn, focusElement);
			});

		document.body.classList.add("cart-overflow-hidden");
		document.body.classList.add("overflow-hidden");
    document.dispatchEvent(new CustomEvent('cart:drawer-opened', {detail: {drawer: this}}));
	}

  close() {
    this.cancelOpenMotion?.();
    if (this.cancelNotificationCleanup && !this.classList.contains('active')) return;
    const wasActive = this.classList.contains('active');
    this.classList.remove('active');
    if (wasActive) document.dispatchEvent(new CustomEvent('cart:drawer-closed', {detail: {drawer: this}}));
    document.removeEventListener('keydown', this.onTrapKeydown, true);
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('cart-overflow-hidden');
    releaseThemeScrollLock();

    // Keep the notification layout until the sliding panel is off screen.
    const panel = this.querySelector('.drawer__inner');
    const notification = this.querySelector('.notification');
    const activeRows = [...this.querySelectorAll('.cart-item.active')];
    let timer;
    const cancel = () => {
      clearTimeout(timer);
      panel?.removeEventListener('transitionend', onEnd);
      if (this.cancelNotificationCleanup === cancel) this.cancelNotificationCleanup = null;
    };
    const finish = () => {
      if (this.cancelNotificationCleanup !== cancel) return;
      cancel();
      if (this.classList.contains('active')) return;
      notification?.classList.remove('notification-open');
      activeRows.forEach(row => row.classList.remove('active'));
    };
    const onEnd = event => {
      if (event.target === panel && event.propertyName === 'transform') finish();
    };
    this.cancelNotificationCleanup?.();
    this.cancelNotificationCleanup = cancel;
    const style = panel && getComputedStyle(panel);
    const milliseconds = value => parseFloat(value) * (value.trim().endsWith('ms') ? 1 : 1000) || 0;
    const durations = (style?.transitionDuration || '0s').split(',').map(milliseconds);
    const delays = (style?.transitionDelay || '0s').split(',').map(milliseconds);
    const properties = (style?.transitionProperty || 'all').split(',').map(value => value.trim());
    const duration = Math.max(0, ...properties.map((property, index) =>
      property === 'all' || property === 'transform'
        ? durations[index % durations.length] + delays[index % delays.length] : 0));
    if (!duration) { finish(); return; }
    panel.addEventListener('transitionend', onEnd);
    // Hidden/replaced panels may not emit transitionend. Reopening cancels both paths.
    timer = setTimeout(finish, duration + 50);
  }

  disconnectedCallback() {
    if (this.classList.contains('active')) this.close();
    document.removeEventListener('keydown', this.onTrapKeydown, true);
    this.cancelOpenMotion?.();
    this.cancelNotificationCleanup?.();
  }

	setSummaryAccessibility(cartDrawerNote) {
		cartDrawerNote.setAttribute("role", "button");
		cartDrawerNote.setAttribute("aria-expanded", "false");

		if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
			cartDrawerNote.setAttribute(
				"aria-controls",
				cartDrawerNote.nextElementSibling.id
			);
		}

		cartDrawerNote.addEventListener("click", (event) => {
			event.currentTarget.setAttribute(
				"aria-expanded",
				!event.currentTarget.closest("details").hasAttribute("open")
			);
		});

		cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
	}

  async renderContents(parsedState, {refreshed = false} = {}) {
    if (!refreshed) await CartFlow.refresh();
    // A confirmed add still needs feedback when section rendering is unavailable.
    if (CartFlow.displayStale) { this.open(); return; }
    this.productId = parsedState.id || parsedState.items?.[0]?.id;
    const key = parsedState.key || parsedState.items?.[0]?.key;
    const row = [...this.querySelectorAll('.cart-item')].find(item => key ? item.dataset.key === key : item.dataset.itemId === String(this.productId));
    this.querySelectorAll('.cart-item.active').forEach(item => item.classList.remove('active'));
    this.querySelector('.notification')?.classList.toggle('notification-open', !!row);
    row?.classList.add('active');
    this.open();
  }

	getSectionInnerHTML(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector).innerHTML;
	}

	getSectionsToRender() {
		return [
			{
				id: "cart-drawer",
				selector: "#CartDrawer",
			},
			{
				id: "cart-icon-bubble",
			},
		];
	}

	getSectionDOM(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector);
	}

	setActiveElement(element) {
		this.activeElement = element;
	}
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
	getSectionsToRender() {
		return [
			{
				id: "CartDrawer",
				section: "cart-drawer",
				selector: ".drawer__inner",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: ".shopify-section",
			},
		];
	}
}

customElements.define("cart-drawer-items", CartDrawerItems);

// Backward-compatible entry points used by swatch and VO callers.
function scheduleCartSync() {
  return cartDataChange();
}
function cartSyncNow() {
  return cartDataChange();
}
$(document).on('click', '.main-cart .gfgFreeGiftReadyToClaimButton, .drawer .gfgFreeGiftReadyToClaimButton', function () {
  setTimeout(() => CartFlow.refresh().catch(error => CartFlow.error(error.message)), 1000);
});
$(document).on('click', '.coupon .success span:last-of-type', function () { $(this).parent().hide(); });
